/**
 * Settings search's index (M286 idea 12): what ⌘K / Ctrl-K and the hub's search box can find, each with the URL that
 * opens it. Only what this person may open: the hub entries they pass the gate of (useVisibleSettingsEntries), and
 * inside them the tabs of Security & Permissions (Widget Permissions for the admin roles, as the page), the Data
 * curation tabs they open (useDataCurationTabs) with Property Types' own tabs and the Reference Data sections, the App
 * settings groups and settings the server would let them read (settings-access.ts), the AI tabs with the ai.* groups
 * on Caps & corrections, and the Feature Access rows (the feature catalog). No fuzzy matching: every typed word must appear
 * (matchesAllTerms); an item whose title holds them all ranks first.
 */
import { useMemo } from 'react';
import { SETTINGS_REGISTRY, type SettingsGroup } from '@aiga/app-config/settings';
import { ADMIN_ROLES, FEATURE_CATALOG, FEATURE_MODULES } from '@aiga/app-config/permissions';
import { matchesAllTerms } from '@/config/search/search-utils';
import { useAuth } from '@/contexts/AuthContext';
import { useIsSuperAdmin } from '@/hooks/useIsSuperAdmin';
import { useSkipsPermissionChecks } from '@/hooks/useSkipsPermissionChecks';
import { isAppSettingsGroupVisible } from '../settings-registry/useCanSeeAppSettings';
import { REFERENCE_DATA_GROUPS, REFERENCE_SECTION_HASH } from './referenceDataSections';
import { APP_SETTINGS_PATH, type SettingsEntry } from './settingsEntries';
import {
  AI_CAPS_GROUPS,
  AI_PATH,
  AI_TABS,
  DATA_CURATION_PATH,
  DATA_CURATION_TABS,
  PERMISSION_TABS,
  PROPERTY_TYPES_TAB_PARAM,
  PROPERTY_TYPES_TABS,
  type DataCurationTabId,
} from './settingsTabs';
import { useDataCurationTabs } from './useDataCurationAccess';
import { useVisibleSettingsEntries } from './useVisibleSettingsEntries';

/** Results are grouped by kind, in this order when they rank alike. */
export const SETTINGS_SEARCH_KINDS = ['page', 'setting', 'reference'] as const;
export type SettingsSearchKind = (typeof SETTINGS_SEARCH_KINDS)[number];
export const SETTINGS_SEARCH_KIND_LABELS: Record<SettingsSearchKind, string> = { page: 'Pages', setting: 'Settings', reference: 'Reference data' };

export interface SettingsSearchItem {
  id: string;
  kind: SettingsSearchKind;
  title: string;
  /** Where it lives, e.g. "App settings › User invitations". */
  crumb: string;
  description: string;
  /** More words that match but are not shown: sub-options, setting keys. */
  keywords: string;
  /** The URL that opens it: a route, a `?tab=` or a `#` anchor. */
  to: string;
  /** A hub tile: listed before anything is typed. */
  hubTile?: true;
}

const PERMISSIONS_PATH = '/settings/permissions';
const SEARCH_FIELDS = ['title', 'crumb', 'description', 'keywords'];

interface IndexAccess {
  /** The role is one of ADMIN_ROLES (Widget Permissions). */
  isAdmin: boolean;
  /** The Data curation tabs they open. */
  curationTabs: readonly DataCurationTabId[];
  /** The App settings group is on the page for them and the server would let them read it. */
  canReadGroup: (group: SettingsGroup) => boolean;
}

/** The index for the hub entries a person sees: everything inside an entry is reachable only when the entry is. */
export function buildSettingsSearchIndex(entries: readonly SettingsEntry[], access: IndexAccess): SettingsSearchItem[] {
  const has = (path: string) => entries.find((e) => e.path === path);
  const items: SettingsSearchItem[] = entries.map((e) => ({
    id: `entry:${e.path}`,
    kind: 'page',
    title: e.title,
    crumb: 'Settings',
    description: e.description,
    keywords: e.subOptions.join(' '),
    to: e.path,
    hubTile: true,
  }));

  const permissions = has(PERMISSIONS_PATH);
  if (permissions) {
    for (const t of PERMISSION_TABS) {
      if (t.adminOnly && !access.isAdmin) continue;
      items.push({ id: `tab:permissions:${t.id}`, kind: 'page', title: t.label, crumb: permissions.title, description: '', keywords: '', to: `${PERMISSIONS_PATH}?tab=${t.id}` });
    }
  }
  const curation = has(DATA_CURATION_PATH);
  const curationTabs = curation ? DATA_CURATION_TABS.filter((t) => access.curationTabs.includes(t.id)) : [];
  const curationTab = (id: DataCurationTabId) => curationTabs.find((t) => t.id === id);
  for (const t of curationTabs) {
    items.push({ id: `tab:data-curation:${t.id}`, kind: 'page', title: t.label, crumb: curation?.title ?? '', description: '', keywords: '', to: `${DATA_CURATION_PATH}?tab=${t.id}` });
  }
  const propertyTypes = curationTab('types');
  if (propertyTypes) {
    for (const t of PROPERTY_TYPES_TABS) {
      const to = `${DATA_CURATION_PATH}?tab=types&${PROPERTY_TYPES_TAB_PARAM}=${t.id}`;
      items.push({ id: `tab:property-types:${t.id}`, kind: 'page', title: t.label, crumb: `${curation?.title} › ${propertyTypes.label}`, description: '', keywords: '', to });
    }
  }

  // A settings group and its settings, each opening SettingsGroupForm's anchor: the page scrolls to it once its groups load.
  const pushGroup = (g: SettingsGroup, crumb: string, page: string) => {
    const to = `${page}#settings-group-${g.id}`;
    const settings = Object.values(g.settings);
    items.push({ id: `group:${g.id}`, kind: 'setting', title: g.label, crumb, description: '', keywords: settings.map((s) => s.label).join(' '), to });
    for (const s of settings) {
      items.push({ id: `setting:${s.key}`, kind: 'setting', title: s.label, crumb: `${crumb} › ${g.label}`, description: s.help ?? '', keywords: s.key, to });
    }
  };

  const appSettings = has(APP_SETTINGS_PATH);
  if (appSettings) {
    for (const g of SETTINGS_REGISTRY.groups) if (access.canReadGroup(g)) pushGroup(g, appSettings.title, APP_SETTINGS_PATH);
  }

  // Settings → AI is AIG-only (its gate), and an AIG account reads every group, so its tabs and groups need no check of their own.
  const ai = has(AI_PATH);
  if (ai) {
    for (const t of AI_TABS) items.push({ id: `tab:ai:${t.id}`, kind: 'page', title: t.label, crumb: ai.title, description: '', keywords: '', to: `${AI_PATH}?tab=${t.id}` });
    const caps = AI_TABS.find((t) => t.id === 'caps')!;
    for (const id of AI_CAPS_GROUPS) {
      const g = SETTINGS_REGISTRY.group(id);
      if (g) pushGroup(g, `${ai.title} › ${caps.label}`, `${AI_PATH}?tab=caps`);
    }
  }

  if (permissions) {
    for (const f of FEATURE_CATALOG) {
      items.push({
        id: `feature:${f.key}`,
        kind: 'setting',
        title: f.label,
        crumb: `${permissions.title} › Feature Access`,
        description: f.description,
        keywords: FEATURE_MODULES[f.module],
        to: `${PERMISSIONS_PATH}?tab=features`,
      });
    }
  }

  const referenceData = curationTab('lookups');
  if (referenceData) {
    // The lookups tab is the page's first, so its section anchors need no ?tab=.
    for (const g of REFERENCE_DATA_GROUPS) {
      for (const s of g.sections) {
        items.push({
          id: `reference:${s.key}`,
          kind: 'reference',
          title: s.title,
          crumb: `${referenceData.label} › ${g.title}`,
          description: s.blurb,
          keywords: s.entityLabelPlural,
          to: `${DATA_CURATION_PATH}${REFERENCE_SECTION_HASH}${s.key}`,
        });
      }
    }
  }
  return items;
}

export interface SettingsSearchGroup {
  kind: SettingsSearchKind;
  items: SettingsSearchItem[];
}

/**
 * The items every word of `query` appears in, grouped by kind. Inside a group a title match comes first; the group
 * holding the best match comes first (then Pages · Settings · Reference data), so the first result is the best one.
 * Nothing typed: the hub tiles.
 */
export function searchSettingsIndex(items: readonly SettingsSearchItem[], query: string): SettingsSearchGroup[] {
  const q = query.trim();
  const ranked = items.flatMap((item, order) => {
    if (!q) return item.hubTile ? [{ item, rank: 0, order }] : [];
    if (!matchesAllTerms(q, item, SEARCH_FIELDS)) return [];
    return [{ item, rank: matchesAllTerms(q, item, ['title']) ? 0 : 1, order }];
  });
  return SETTINGS_SEARCH_KINDS.map((kind) => {
    const hits = ranked.filter((r) => r.item.kind === kind).sort((a, b) => a.rank - b.rank || a.order - b.order);
    return { kind, best: hits[0]?.rank ?? Infinity, items: hits.map((r) => r.item) };
  })
    .filter((g) => g.items.length > 0)
    .sort((a, b) => a.best - b.best || SETTINGS_SEARCH_KINDS.indexOf(a.kind) - SETTINGS_SEARCH_KINDS.indexOf(b.kind))
    .map(({ kind, items: groupItems }) => ({ kind, items: groupItems }));
}

/** This person's index. */
export function useSettingsSearchIndex(): SettingsSearchItem[] {
  const entries = useVisibleSettingsEntries();
  const { role, permissions } = useAuth();
  const skips = useSkipsPermissionChecks();
  const isAig = useIsSuperAdmin();
  const isAdmin = !!role?.name && ADMIN_ROLES.has(role.name);
  const curationTabs = useDataCurationTabs();

  return useMemo(() => {
    const held = new Set((permissions ?? []).map((p) => p.name));
    // The server's read rule for a group (settings-access.ts), and only the groups App settings shows.
    const canReadGroup = (g: SettingsGroup) =>
      isAppSettingsGroupVisible(g.id, isAig) && (skips || held.has(g.editPermission) || g.readPermission.some((p) => held.has(p)));
    return buildSettingsSearchIndex(entries, { isAdmin, curationTabs, canReadGroup });
  }, [entries, permissions, skips, isAig, isAdmin, curationTabs]);
}
